import {
  activityAnchor,
  commentAnchor,
  permalinkResponseSchema,
  type PermalinkResponse,
} from '@molt/shared';

import { get } from './api';
import type { InterfaceLanguageContextValue } from './interface-language';

export type PermalinkTarget = 'comment' | 'activity';

export interface ParsedPermalinkHash {
  readonly target: PermalinkTarget;
  readonly id: string;
}

/**
 * What `GET /comments/:id/permalink` and `GET /activity/:id/permalink` return —
 * `B61`, `FR-COLLAB-012`.
 *
 * **An address, never content.** The resolver returns the target kind, status,
 * ids, timestamp and canonical board/card addresses, and never comment text, an
 * activity payload or an actor profile. That is what lets it be
 * anonymous-capable by board visibility: on a public board an anonymous caller
 * resolves both activity target shapes, and gets byte-identically what a member
 * gets (pinned in `apps/server/src/routes/permalinks.integration.test.ts`).
 * `z.strictObject` is the client half of that promise — an `actor` or `payload`
 * key would be refused here rather than parsed and rendered.
 *
 * The schema is imported from `@molt/shared`, which keeps the client parse
 * boundary byte-for-byte aligned with the Fastify response schema.
 */
export { permalinkResponseSchema, type PermalinkResponse };

export function getCommentPermalink(commentId: string): Promise<PermalinkResponse> {
  return get(
    `/comments/${encodeURIComponent(commentId)}/permalink`,
    permalinkResponseSchema,
  );
}

export function getActivityPermalink(activityId: string): Promise<PermalinkResponse> {
  return get(
    `/activity/${encodeURIComponent(activityId)}/permalink`,
    permalinkResponseSchema,
  );
}

export function permalinkPath(
  t: InterfaceLanguageContextValue['t'],
  response: PermalinkResponse,
): string {
  const basePath =
    response.targetType === 'card' ? response.card?.path : response.board.path;
  const id = response.target === 'comment' ? response.commentId : response.activityId;

  if (basePath === undefined || id === undefined) {
    throw new Error(t('primitives.permalink.missingTargetPath'));
  }

  const hash = response.target === 'comment' ? commentAnchor(id) : activityAnchor(id);
  return `${basePath}#${hash}`;
}

export function parsePermalinkHash(hash: string): ParsedPermalinkHash | null {
  const value = hash.startsWith('#') ? hash.slice(1) : hash;
  if (value.startsWith('comment-')) {
    const id = value.slice('comment-'.length);
    return id.length === 0 ? null : { target: 'comment', id };
  }
  if (value.startsWith('activity-')) {
    const id = value.slice('activity-'.length);
    return id.length === 0 ? null : { target: 'activity', id };
  }
  return null;
}

export function absolutePermalink(path: string, origin = browserOrigin()): string {
  return `${origin}${path}`;
}

export async function copyTextToClipboard(value: string): Promise<boolean> {
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  if (clipboard?.writeText === undefined) return false;

  try {
    await clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function browserOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}
